import type { MailFolder } from 'owa-graph-schema';
import folderStore, { getEffectiveFolderDisplayName } from 'owa-folders';
import publicFolderFavoriteStore from 'owa-public-folder-favorite/lib/store/publicFolderFavoriteStore';
import loc, { format } from 'owa-localize';
import { isMonarchMultipleAccountsEnabled } from 'owa-account-source-list';
import { getStore as getSharedFavoritesStore } from 'owa-favorites';
import type { FavoriteFolderData, FavoritePublicFolderData } from 'owa-favorites-types';
import {
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
} from 'owa-folders-constants';
import {
    siderailFavoriteTooltipTemplate,
    siderailFavoriteTooltipAccountSuffixTemplate,
} from './FavoriteDisplayName.locstring.json';
import { getMailboxLabelForFavorite } from './getMailboxLabelForFavorite';

const PATH_SEPARATOR = ' > ';
const PATH_ELLIPSIS = '...';
// Per spec: show first two and last one ancestor when depth exceeds this threshold.
const MAX_PATH_PARTS_BEFORE_ELLIPSIS = 3;
// Root mailbox names returned by various Exchange backends. We stop the ancestor walk
// here so the user-facing path never includes "Top of Information Store" (or its
// internal alias `msgfolderroot`).
const ROOT_FOLDER_NAMES = new Set(['msgfolderroot', 'top of information store']);
const ROOT_DISTINGUISHED_FOLDER_TYPES = new Set<string>([
    PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID,
    ARCHIVE_FOLDER_ROOT_DISTINGUISHED_ID,
]);

function getFolder(folderId: string): MailFolder | undefined {
    if (publicFolderFavoriteStore().folderTable.has(folderId)) {
        return publicFolderFavoriteStore().folderTable.get(folderId);
    }
    return folderStore().folderTable.get(folderId);
}

function getFolderIdForFavorite(favoriteId: string): string | undefined {
    const data = getSharedFavoritesStore().outlookFavorites.get(favoriteId);
    switch (data?.type) {
        case 'folder':
        case 'localFolderArchive':
        case 'localFolderShared':
        case 'localFolderPst':
            return (data as FavoriteFolderData).folderId;
        case 'publicFolder':
            return (data as FavoritePublicFolderData).publicFolderId;
        default:
            return undefined;
    }
}

function isMailboxRoot(folder: MailFolder): boolean {
    if (
        folder.distinguishedFolderType &&
        ROOT_DISTINGUISHED_FOLDER_TYPES.has(folder.distinguishedFolderType)
    ) {
        return true;
    }
    const name = getEffectiveFolderDisplayName(folder);
    return !name || ROOT_FOLDER_NAMES.has(name.toLocaleLowerCase());
}

function buildAncestorPath(folder: MailFolder): string {
    const ancestors: string[] = [];
    let current: MailFolder | undefined = folder;
    while (current && !isMailboxRoot(current)) {
        ancestors.unshift(getEffectiveFolderDisplayName(current) ?? '');
        current = current.parentFolderId ? getFolder(current.parentFolderId) : undefined;
    }

    if (ancestors.length === 0) {
        return '';
    }

    if (ancestors.length > MAX_PATH_PARTS_BEFORE_ELLIPSIS) {
        return [ancestors[0], ancestors[1], PATH_ELLIPSIS, ancestors[ancestors.length - 1]].join(
            PATH_SEPARATOR
        );
    }

    return ancestors.join(PATH_SEPARATOR);
}

/**
 * Produces the tooltip text for a favorite folder rendered in the vertical side rail.
 * Format:
 *   - Single account:                          "Inbox - 12 items (3 unread)"
 *   - Nested folder:                           "Inbox > Subfolder - 12 items (3 unread)"
 *   - Monarch multi-account (any folder):      "Inbox (Microsoft) - 12 items (3 unread)"
 *   - Deep nesting (> 3 ancestors):            "Inbox > Subfolder1 > ... > Subfolder4 - 12 items (3 unread)"
 *
 * The account label is appended whenever Monarch multi-account mode is active so the
 * user always knows which mailbox the favorite belongs to, falling back to the SMTP
 * address if no friendly label is available.
 */
export default function getSiderailFavoriteTooltipText(favoriteId: string): string {
    const folderId = getFolderIdForFavorite(favoriteId);
    if (!folderId) {
        return '';
    }

    const folder = getFolder(folderId);
    if (!folder) {
        return '';
    }

    const leafName = getEffectiveFolderDisplayName(folder) ?? '';
    const path = buildAncestorPath(folder) || leafName;

    let nameWithDisambiguation = path;
    if (isMonarchMultipleAccountsEnabled() && folder.mailboxInfo) {
        const accountLabel =
            getMailboxLabelForFavorite(folder.mailboxInfo) ?? folder.mailboxInfo.mailboxSmtpAddress;
        if (accountLabel) {
            nameWithDisambiguation = format(
                loc(siderailFavoriteTooltipAccountSuffixTemplate),
                path,
                accountLabel
            );
        }
    }

    const totalCount = folder.totalMessageCount ?? 0;
    const unreadCount = folder.UnreadCount ?? 0;

    return format(
        loc(siderailFavoriteTooltipTemplate),
        nameWithDisambiguation,
        totalCount,
        unreadCount
    );
}
